import { useEffect, useState } from "react";
import api from "../lib/api";
import { ArrowRight, Gavel } from "lucide-react";
import { toast } from "sonner";

export default function DefendersJoin() {
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", firm: "", jurisdiction: "",
    bar_years: 5, trials_tried: 0, practice_areas: [],
    accepts_no_plea_creed: false, has_filed_section_1983: false,
    philosophy: "", case_examples: "", website: "",
  });
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/defenders/practice-areas").then(r => setAreas(r.data)); }, []);

  const toggleArea = (slug) => {
    setForm(f => ({
      ...f,
      practice_areas: f.practice_areas.includes(slug)
        ? f.practice_areas.filter(s => s !== slug)
        : [...f.practice_areas, slug],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accepts_no_plea_creed) { toast.error("The no-plea creed is non-negotiable."); return; }
    if (form.practice_areas.length === 0) { toast.error("Select at least one practice area."); return; }
    try {
      const { data } = await api.post("/defenders/apply", form);
      setResult(data);
      toast.success("Application submitted to the Founder.");
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed."); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="defenders-join">
      <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Membership Application</div>
      <h1 className="display-black text-4xl lg:text-5xl uppercase text-[#064E3B] mt-3 tracking-tighter">Join Available Defenders.</h1>
      <p className="text-gray-700 mt-3 max-w-2xl">Founder reviews every application personally. The creed is non-negotiable.</p>

      {result ? (
        <div className="mt-10 border border-[#064E3B] bg-gray-50 p-10" data-testid="join-success">
          <Gavel className="w-10 h-10 text-[#D97706]" />
          <h2 className="display-black text-2xl uppercase text-[#064E3B] mt-4">
            {result.qualified_screening ? "★ Pre-Qualified · Sent to Founder" : "Application Received"}
          </h2>
          <p className="text-gray-800 mt-3">{result.message}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 border border-gray-300 p-7" data-testid="defenders-join-form">
          <input className="input-federal" required placeholder="Full name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="dj-name" />
          <input className="input-federal" required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="dj-email" />
          <input className="input-federal" required placeholder="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="dj-phone" />
          <input className="input-federal" placeholder="Website / Firm URL" value={form.website} onChange={e => setForm({...form, website: e.target.value})} data-testid="dj-website" />
          <input className="input-federal md:col-span-2" required placeholder="Firm / Independent practice" value={form.firm} onChange={e => setForm({...form, firm: e.target.value})} data-testid="dj-firm" />
          <input className="input-federal md:col-span-2" required placeholder="Jurisdiction (Circuit / State / City)" value={form.jurisdiction} onChange={e => setForm({...form, jurisdiction: e.target.value})} data-testid="dj-jurisdiction" />
          <input className="input-federal" required type="number" min={1} placeholder="Years at the bar" value={form.bar_years} onChange={e => setForm({...form, bar_years: parseInt(e.target.value) || 1})} data-testid="dj-bar-years" />
          <input className="input-federal" required type="number" min={0} placeholder="Trials tried" value={form.trials_tried} onChange={e => setForm({...form, trials_tried: parseInt(e.target.value) || 0})} data-testid="dj-trials" />

          <div className="md:col-span-2">
            <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#064E3B] mb-3">Practice Areas (select all that apply)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {areas.map(a => (
                <label key={a.slug} className="flex items-start gap-3 border border-gray-300 p-3 cursor-pointer hover:border-[#064E3B]" data-testid={`dj-area-${a.slug}`}>
                  <input type="checkbox" checked={form.practice_areas.includes(a.slug)} onChange={() => toggleArea(a.slug)} className="mt-1 w-4 h-4" />
                  <div>
                    <div className="font-bold text-sm text-[#064E3B]">{a.name}</div>
                    <div className="text-xs text-gray-700 mt-1">{a.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <textarea className="input-federal md:col-span-2" rows={4} required placeholder="Your philosophy on plea bargains, prosecutor misconduct, and §1983 litigation" value={form.philosophy} onChange={e => setForm({...form, philosophy: e.target.value})} data-testid="dj-philosophy" />
          <textarea className="input-federal md:col-span-2" rows={4} placeholder="Notable cases (citations + 1-line outcomes — optional)" value={form.case_examples} onChange={e => setForm({...form, case_examples: e.target.value})} data-testid="dj-cases" />

          <div className="md:col-span-2 space-y-3 border-t border-gray-200 pt-5">
            <label className="flex items-start gap-3 text-sm text-gray-900" data-testid="dj-creed">
              <input type="checkbox" checked={form.accepts_no_plea_creed} onChange={e => setForm({...form, accepts_no_plea_creed: e.target.checked})} className="mt-1 w-4 h-4" />
              <span>
                <span className="font-bold text-[#D97706]">THE CREED:</span> I do not recommend pleas as a first move. I file motions. I prepare for trial. I do not abandon clients to the plea mill.
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-gray-900" data-testid="dj-1983">
              <input type="checkbox" checked={form.has_filed_section_1983} onChange={e => setForm({...form, has_filed_section_1983: e.target.checked})} className="mt-1 w-4 h-4" />
              <span>I have filed or co-counselled at least one §1983 civil-rights complaint (or post-Thompson malicious-prosecution claim).</span>
            </label>
          </div>

          <button type="submit" className="btn-signal md:col-span-2 justify-center" data-testid="dj-submit">Submit Application <ArrowRight className="w-4 h-4" /></button>
        </form>
      )}
    </div>
  );
}
